/**
 * ConnectorGate — render children only when the connector is `enabled`.
 * Otherwise renders a friendly empty state inviting the operator to enable
 * the connector from Settings → Connectors. Used to wrap entire page
 * surfaces (Alerts, Agents, etc.).
 */
import { Link } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CONNECTOR_REGISTRY } from "@/connectors/registry";
import { useConnector } from "@/hooks/connectors/useConnector";
import type { ConnectorId } from "@/types/connectors";

interface ConnectorGateProps {
  connectorId: ConnectorId;
  children: React.ReactNode;
}

export function ConnectorGate({ connectorId, children }: ConnectorGateProps) {
  const state = useConnector(connectorId);
  const def = CONNECTOR_REGISTRY[connectorId];

  if (state.status === "enabled") return <>{children}</>;

  return (
    <div className="px-6 py-6">
      <div className="mx-auto max-w-xl rounded-lg border border-dashed border-border bg-muted/30 p-10 text-center">
        <ShieldOff className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
        <h2 className="mt-3 text-lg font-semibold text-foreground">
          {def.name} isn't enabled yet
        </h2>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          {def.tagline} Enable the connector from Settings to unlock this area.
        </p>
        <div className="mt-5">
          <Button asChild>
            <Link to={`/settings/connectors/${connectorId}`}>Open connector</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
